/**
 * Header / navigation behaviour (Phase 1.1, issue #21).
 *
 * Progressive enhancement, vanilla JS. The header is fully usable without JS:
 * the dropdown opens on :hover / :focus-within (CSS), links navigate normally,
 * and the off-canvas panel CSS degrades to a visible stacked list when the
 * toggle button is absent. This script layers on:
 *
 *   1. Scroll state   — toggles `.c-header--scrolled` on the `.js-nav` root once
 *                       the page is scrolled past a threshold (Home → Scroll).
 *   2. Mobile menu     — `.js-nav-toggle` opens/closes the off-canvas panel,
 *                       syncing `aria-expanded`; Escape closes; focus is moved
 *                       into the panel on open and returned to the toggle on
 *                       close; clicking the backdrop closes it.
 *   3. Dropdowns       — `.js-nav-dropdown` triggers toggle `.is-open` on their
 *                       parent item and sync `aria-expanded`, so keyboard users
 *                       (and touch) get an explicit open/close in addition to
 *                       the CSS hover path. Escape / outside-click closes.
 *
 * `js-` hooks are behaviour-only and are never styled.
 */
(function () {
  'use strict';

  var nav = document.querySelector('.js-nav');
  if (!nav) {
    return;
  }

  // ---------------------------------------------------------------------------
  // 1. Scroll state — Home → Scroll restyle.
  // ---------------------------------------------------------------------------
  var SCROLL_THRESHOLD = 24; // px scrolled before switching to the Scroll state
  var scrollTicking = false;

  function syncScrollState() {
    var scrolled = window.scrollY > SCROLL_THRESHOLD;
    nav.classList.toggle('c-header--scrolled', scrolled);
    scrollTicking = false;
  }

  function onScroll() {
    if (!scrollTicking) {
      scrollTicking = true;
      window.requestAnimationFrame(syncScrollState);
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  syncScrollState(); // initialise (handles reload mid-page)

  // ---------------------------------------------------------------------------
  // 2. Mobile off-canvas menu.
  // ---------------------------------------------------------------------------
  var toggle = nav.querySelector('.js-nav-toggle');
  var panel = nav.querySelector('.js-nav-menu');
  var backdrop = nav.querySelector('.js-nav-backdrop');

  // Focusable elements inside the panel, used for simple focus management.
  var FOCUSABLE =
    'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';

  function isMenuOpen() {
    return nav.classList.contains('is-menu-open');
  }

  function openMenu() {
    if (!toggle) {
      return;
    }
    nav.classList.add('is-menu-open');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', 'Menu sluiten');

    // Move focus to the first focusable item in the panel.
    if (panel) {
      var first = panel.querySelector(FOCUSABLE);
      if (first) {
        first.focus();
      }
    }
    document.addEventListener('keydown', onMenuKeydown);
  }

  function closeMenu(returnFocus) {
    if (!toggle) {
      return;
    }
    nav.classList.remove('is-menu-open');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Menu openen');
    document.removeEventListener('keydown', onMenuKeydown);
    if (returnFocus !== false) {
      toggle.focus();
    }
  }

  // Rudimentary focus trap: keep Tab cycling inside the open panel.
  function trapFocus(event) {
    if (!panel) {
      return;
    }
    var items = Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE));
    if (items.length === 0) {
      return;
    }
    var firstItem = items[0];
    var lastItem = items[items.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && active === firstItem) {
      event.preventDefault();
      lastItem.focus();
    } else if (!event.shiftKey && active === lastItem) {
      event.preventDefault();
      firstItem.focus();
    }
  }

  function onMenuKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      closeMenu();
    } else if (event.key === 'Tab') {
      trapFocus(event);
    }
  }

  if (toggle) {
    // The button is interactive only with JS; expose its semantics now.
    toggle.setAttribute('aria-expanded', 'false');

    toggle.addEventListener('click', function () {
      if (isMenuOpen()) {
        closeMenu();
      } else {
        openMenu();
      }
    });
  }

  if (backdrop) {
    backdrop.addEventListener('click', function () {
      closeMenu();
    });
  }

  // Close the panel when a menu link is followed (mobile UX).
  if (panel) {
    panel.addEventListener('click', function (event) {
      var link = event.target.closest('a[href]');
      if (link && isMenuOpen()) {
        closeMenu(false);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // 3. Dropdown triggers (keyboard / touch explicit toggle).
  // ---------------------------------------------------------------------------
  var dropdownTriggers = nav.querySelectorAll('.js-nav-dropdown');

  function closeAllDropdowns(except) {
    Array.prototype.forEach.call(dropdownTriggers, function (trigger) {
      if (trigger === except) {
        return;
      }
      var parent = trigger.closest('.c-header__item');
      if (parent) {
        parent.classList.remove('is-open');
      }
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  Array.prototype.forEach.call(dropdownTriggers, function (trigger) {
    var parent = trigger.closest('.c-header__item');
    if (!parent) {
      return;
    }
    trigger.setAttribute('aria-expanded', 'false');

    trigger.addEventListener('click', function (event) {
      // Only intercept when the trigger acts as a button (no real href target),
      // or when it carries aria-haspopup — otherwise let the link navigate.
      var isButton = trigger.tagName === 'BUTTON';
      var hasPopup = trigger.getAttribute('aria-haspopup') === 'true';
      if (!isButton && !hasPopup) {
        return;
      }
      event.preventDefault();
      var willOpen = !parent.classList.contains('is-open');
      closeAllDropdowns(trigger);
      parent.classList.toggle('is-open', willOpen);
      trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });

    // Escape closes the dropdown and returns focus to its trigger.
    parent.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        if (parent.classList.contains('is-open')) {
          parent.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.focus();
        }
      }
    });
  });

  // Close open dropdowns when clicking outside the nav (desktop).
  document.addEventListener('click', function (event) {
    if (!nav.contains(event.target)) {
      closeAllDropdowns(null);
    }
  });
})();
